<template>
  <div class="card pb-3 mb-0 h-full">
    <!-- Brand Header -->
    <div
      class="px-4 py-3 rounded-top border-bottom"
      :class="{
        'bg-primary text-white': isActiveCompany,
        'bg-white text-dark': !isActiveCompany
      }"
    >
      <h2 class="h3 fw-semibold mb-0 text-truncate" :title="brand.name">
        {{ brand.name | truncate(18) }}
      </h2>
    </div>

    <!-- Top Strengths -->
    <div class="px-4 pt-3">
      <h5 class="text-muted">Top Strengths</h5>
      <div class="min-h-[78px]">
        <!-- Show actual strengths -->
        <div v-for="metric in brand.topStrengths" :key="'pos-' + metric.categoryId" class="mb-2">
          <button
            class="btn btn-link p-0 text-start text-decoration-none w-100"
            @click="onCategoryClick(metric, 'POSITIVE')"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <span class="text-muted d-inline-block" :style="{ width: '40px' }"
                  >{{ metric.positiveSentimentPercentage }}%</span
                >
                <span class="text-success">{{ formatCategoryName(metric.categoryId) }}</span>
              </div>
              <i class="far fa-arrow-right text-success"></i>
            </div>
          </button>
        </div>

        <!-- Show "no matched" once if no data -->
        <div v-if="!brand.topStrengths || brand.topStrengths.length === 0" class="mb-2">
          <div class="d-flex align-items-center text-muted" :style="{ height: '24px' }">
            <span :style="{ whiteSpace: 'nowrap' }">- No matched strengths</span>
          </div>
        </div>

        <!-- Fill remaining space to keep consistent height -->
        <div v-for="n in remainingStrengthSlots" :key="'empty-pos-' + n" class="mb-2">
          <div :style="{ height: '24px' }"></div>
        </div>
      </div>
    </div>

    <!-- Top Issues -->
    <div class="px-4 pt-3">
      <h5 class="text-muted mb-3">Top Issues</h5>
      <div class="min-h-[78px]">
        <!-- Show actual weaknesses -->
        <div v-for="metric in brand.topWeaknesses" :key="'neg-' + metric.categoryId" class="mb-2">
          <button
            class="btn btn-link p-0 text-start text-decoration-none w-100"
            @click="onCategoryClick(metric, 'NEGATIVE')"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <span class="text-muted d-inline-block" :style="{ width: '40px' }"
                  >{{ metric.negativeSentimentPercentage }}%</span
                >
                <span class="text-danger">{{ formatCategoryName(metric.categoryId) }}</span>
              </div>
              <i class="far fa-arrow-right text-danger"></i>
            </div>
          </button>
        </div>

        <!-- Show "no matched" once if no data -->
        <div v-if="!brand.topWeaknesses || brand.topWeaknesses.length === 0" class="mb-2">
          <div class="d-flex align-items-center text-muted" :style="{ height: '24px' }">
            <span :style="{ whiteSpace: 'nowrap' }">- No matched issues</span>
          </div>
        </div>

        <!-- Fill remaining space to keep consistent height -->
        <div v-for="n in remainingWeaknessSlots" :key="'empty-neg-' + n" class="mb-2">
          <div :style="{ height: '24px' }"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const CompanyModule = createNamespacedHelpers('company')
const InsightsModule = createNamespacedHelpers('insightsv2')
const CompetitionModule = createNamespacedHelpers('competition')

export default {
  name: 'TopMetricsCard',
  props: {
    brand: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...CompanyModule.mapGetters(['activeCompany']),
    ...InsightsModule.mapGetters(['selectCategory']),
    ...CompetitionModule.mapGetters(['selectFilters']),
    isActiveCompany() {
      return this.brand._id === this.activeCompany._id
    },
    remainingStrengthSlots() {
      const strengthsCount = (this.brand.topStrengths || []).length
      const hasNoDataMessage = strengthsCount === 0 ? 1 : 0
      const usedSlots = strengthsCount + hasNoDataMessage
      return Math.max(0, 3 - usedSlots)
    },
    remainingWeaknessSlots() {
      const weaknessesCount = (this.brand.topWeaknesses || []).length
      const hasNoDataMessage = weaknessesCount === 0 ? 1 : 0
      const usedSlots = weaknessesCount + hasNoDataMessage
      return Math.max(0, 3 - usedSlots)
    }
  },
  methods: {
    formatCategoryName(categoryId) {
      const category = this.selectCategory(categoryId)
      if (!category) return 'Unknown Category'

      if (category.path === '') {
        return category.name
      } else {
        const parentCategory = this.selectCategory(category.path.split('/').pop())
        return parentCategory ? `${parentCategory.name}: ${category.name}` : category.name
      }
    },

    onCategoryClick(metric, sentiment) {
      this.$router.push({
        path: '/competition/details',
        query: {
          dateRange: this.$route.query.dateRange,
          sentiment: sentiment,
          category: metric.categoryId,
          brand: this.brand._id,
          locs: this.selectFilters.locations,
          groups: this.selectFilters.groups
        }
      })
    }
  }
}
</script>
